import { List } from 'immutable'
import { createSelector } from 'reselect'
import { includes } from 'ramda'
import { insertIf } from 'utils/types'
import { getIn, get, has, getInOr } from 'utils/immutable'
import { SERVICE, INTERNAL } from 'constants/featureTypes'
import { CHECPLUS_MOD_WITH_FEATURES } from 'utils/constants'
import {
  UPDATE_FEATURE_ANY_ORG,
  UPDATE_SERVICE_FEATURE_ANY_ORG,
  UPDATE_SERVICE_FEATURE_SAME_ORG,
  EDIT_INTERNAL_FEATURES,
  DUPLICATE_FEATURE,
  DUPLICATE_FEATURE_OTHER_ORG,
  CREATE_FEATURE,
  UPDATE_PLUS_VEHICLE_FEATURE,
  DOWNLOAD_PAR_FILES,
  VIEW_FEATURES,
  EDIT_PLUS_VEHICLE_ECP,
  CREATE_AFTER_MARKET_XMC,
  EDIT_FEATURE_COLLECTION,
  ACTIVATE_FEATURE_COLLECTION,
  OBSOLETE_FEATURE_COLLECTION,
  DUPLICATE_FEATURE_COLLECTION,
  VIEW_FEATURE_COLLECTION_REVISION,
  MANAGE_FAST_FEATURES,
} from 'constants/authorization'
import { userRightsSelector, userRolesSelector } from '.'

const featureSelector = getIn(['feature', 'currentFeature', 'feature'])
const userOrgNetworkSelector = getInOr(List(), [
  'user',
  'userData',
  'orgNetwork',
])

export const getCanViewFeatures = createSelector(
  userRightsSelector,
  has(VIEW_FEATURES)
)

const featureMetaSelector = createSelector(featureSelector, (feature) =>
  feature.first(List())
)

const featureModuleNameSelector = createSelector(
  featureMetaSelector,
  get('moduleName')
)

const featureTypeSelector = createSelector(featureMetaSelector, get('type'))

const featureOrgIdSelector = createSelector(featureMetaSelector, get('orgId'))

export const featureIsSameOrgSelector = createSelector(
  featureOrgIdSelector,
  userOrgNetworkSelector,
  includes
)

export const getCanEditSomeFeatures = createSelector(
  userRightsSelector,
  (rights) =>
    rights.some((right) => Object.values(CREATE_FEATURE).includes(right))
)

export const getCanEditFeature = createSelector(
  userRightsSelector,
  featureIsSameOrgSelector,
  featureModuleNameSelector,
  featureTypeSelector,
  (userRights, isSameOrg, moduleName, type) => {
    const isService = type === SERVICE
    const isInternal = type === INTERNAL
    return [
      UPDATE_FEATURE_ANY_ORG[moduleName],
      ...insertIf(isService, UPDATE_SERVICE_FEATURE_ANY_ORG[moduleName]),
      ...insertIf(
        isSameOrg && isService,
        UPDATE_SERVICE_FEATURE_SAME_ORG[moduleName]
      ),
      ...insertIf(isInternal, EDIT_INTERNAL_FEATURES),
    ].some((right) => userRights.has(right))
  }
)

export const getCanDuplicateFeature = createSelector(
  userRightsSelector,
  userRolesSelector,
  featureIsSameOrgSelector,
  featureModuleNameSelector,
  featureTypeSelector,
  (userRights, roles, isSameOrg, moduleName, type) => {
    // order of checks matters
    if (!isSameOrg && !roles.includes(INTERNAL) && type === SERVICE) {
      return userRights.has(DUPLICATE_FEATURE_OTHER_ORG[moduleName])
    }

    if (!isSameOrg && roles.includes(INTERNAL)) {
      return userRights.has(DUPLICATE_FEATURE_OTHER_ORG[moduleName])
    }

    if (type === SERVICE && isSameOrg) {
      return userRights.has(DUPLICATE_FEATURE[moduleName])
    }

    if (type === INTERNAL && roles.includes(INTERNAL) && isSameOrg) {
      return userRights.has(DUPLICATE_FEATURE[moduleName])
    }

    return false
  }
)

export const getAuthenticatedModulesForVehicleUpdate = createSelector(
  userRightsSelector,
  (userRights) =>
    CHECPLUS_MOD_WITH_FEATURES.filter((moduleName) =>
      userRights.has(UPDATE_PLUS_VEHICLE_FEATURE[moduleName])
    )
)

export const getAuthenticatedModulesForCreation = createSelector(
  userRightsSelector,
  (userRights) =>
    CHECPLUS_MOD_WITH_FEATURES.filter((moduleName) =>
      userRights.has(CREATE_FEATURE[moduleName])
    )
)

export const getCanDownloadParFiles = createSelector(
  userRightsSelector,
  has(DOWNLOAD_PAR_FILES)
)

export const getEditECP = createSelector(
  userRightsSelector,
  has(EDIT_PLUS_VEHICLE_ECP)
)

export const getCanCreateAfterMartketXmc = createSelector(
  userRightsSelector,
  has(CREATE_AFTER_MARKET_XMC)
)

// Collection Lifecycle Authorization Selectors (PBI 150666)
// Tier 2+ users (plusDeveloper, plusEngineer, plusAdmin, admin) can activate
// Tier 3+ users (plusEngineer, plusAdmin, admin) can obsolete
export const getCanActivateCollection = createSelector(
  userRightsSelector,
  (rights) => has(ACTIVATE_FEATURE_COLLECTION, rights)
)

export const getCanObsoleteCollection = createSelector(
  userRightsSelector,
  (rights) => has(OBSOLETE_FEATURE_COLLECTION, rights)
)

export const getCanDuplicateCollection = createSelector(
  userRightsSelector,
  (rights) => has(DUPLICATE_FEATURE_COLLECTION, rights)
)

// WP-6 PBI 144828: Edit collection — Tier 2+ (plusDeveloper, plusEngineer, plusAdmin, admin)
// Note: org-matching is handled in the component since it depends on collection data
export const getCanEditCollection = createSelector(
  userRightsSelector,
  (rights) => has(EDIT_FEATURE_COLLECTION, rights)
)

// Bug 505198: Removed stale checDev gate (was Bug 486028 temporary restriction).
// Now uses the proper view_feature_collection_revision right, consistent with
// getCanActivateCollection and getCanEditCollection selectors.
export const getCanViewCollectionRevision = createSelector(
  userRightsSelector,
  (rights) => has(VIEW_FEATURE_COLLECTION_REVISION, rights)
)

// PBI 481284: Fast Feature Management — internal Admin/PlusAdmin only (WI #502155)
export const getCanManageFastFeatures = createSelector(
  userRightsSelector,
  (rights) => rights.has(MANAGE_FAST_FEATURES)
)
